import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { State } from 'state/store-types';
import User from 'app/scripts/models/user';
import { getUsersOfType } from 'app/scripts/components/moderation-v2/users/state/reducer';
import { TModerationUser } from 'app/scripts/components/moderation-v2/users/state/types';
import { LiveBlogSorting, ILiveBlogState, LiveEvent, LiveBlogFilter } from './types';
import * as thunks from './actions';

const ITEMS_ON_PAGE: number = 10;

const initialState: ILiveBlogState = {
  liveEvents: [],
  isLoading: true,
  sortBy: LiveBlogSorting.UpdateTime,
  timeFilter: LiveBlogFilter.Live,
  // Total Events for the spot (up to 500 when fetched)
  totalEvents: 0,
  pageNum: 1
};

const liveBlogSlice = createSlice({
  name: 'LIVE_BLOG',
  initialState,
  reducers: {
    ['SPOTS/CHANGE_SPOT']: state => initialState,
    changePageSettings: (
      state,
      {
        payload: { pageNum = 1, sortBy = state.sortBy, timeFilter = state.timeFilter }
      }: PayloadAction<{ pageNum?: number; sortBy?: LiveBlogSorting; timeFilter?: LiveBlogFilter }>
    ) => {
      state.pageNum = pageNum;
      state.sortBy = sortBy;
      state.timeFilter = timeFilter;
    }
  },
  extraReducers: builder => {
    builder
      .addCase('SPOTS/CHANGE_SPOT', _ => initialState)
      .addCase(
        thunks.fetchLiveBlogEvents.fulfilled,
        (state, { payload: { liveEvents, totalEvents } }) => {
          state.liveEvents = liveEvents;
          state.totalEvents = totalEvents;
          state.isLoading = false;
        }
      )
      .addCase(thunks.fetchLiveBlogEvents.rejected, state => {
        state.isLoading = false;
      })
      .addCase(thunks.deleteLiveBlogEvent.fulfilled, (state, { payload: { eventId } }) => {
        state.liveEvents = state.liveEvents.filter(liveEvent => liveEvent.id !== eventId);
        state.totalEvents--;
      })
      .addCase(thunks.fetchLiveBlogEvent.fulfilled, (state, { payload: { liveEvent } }) => {
        const fetchedEvent = state.liveEvents.find(event => event.id === liveEvent.id);
        fetchedEvent.isFetched = true;
      })
      .addCase(thunks.createLiveBlogEvent.fulfilled, (state, { payload: { liveEvent } }) => {
        state.liveEvents = [liveEvent, ...state.liveEvents];
        state.totalEvents++;
      })
      .addCase(thunks.editLiveBlogEvent.fulfilled, (state, { payload: { liveEvent } }) => {
        state.liveEvents = state.liveEvents.map((event: LiveEvent) =>
          event.id === liveEvent.id ? liveEvent : event
        );
      });
  }
});

function filterByTimeFilter(timeFilter: LiveBlogFilter) {
  return (event: LiveEvent): boolean => {
    const now = Date.now();

    switch (timeFilter) {
      case LiveBlogFilter.Upcoming:
        return now <= (event.start.time ?? event.start.date) * 1000;
      case LiveBlogFilter.Live:
        return (
          now >= (event.start.time ?? event.start.date) * 1000 &&
          now <= (event.end.time ?? event.end.date) * 1000
        );
      case LiveBlogFilter.Past:
        return now > (event.end.time ?? event.end.date) * 1000;
      case LiveBlogFilter.All:
      default:
        return true;
    }
  };
}

function sortByLiveBlogSorting(sorting: LiveBlogSorting) {
  return (eventA: LiveEvent, eventB: LiveEvent): number => {
    switch (sorting) {
      case LiveBlogSorting.EndingTime:
        const endDateDiff = eventB.end.date - eventA.end.date;
        return endDateDiff === 0 ? eventB.end.hour - eventA.end.hour : endDateDiff;
      case LiveBlogSorting.StartingTime:
        const startDateDiff = eventA.start.date - eventB.start.date;
        return startDateDiff === 0 ? eventA.start.hour - eventB.start.hour : startDateDiff;
      case LiveBlogSorting.UpdateTime:
      default:
        return eventB.createdAt - eventA.createdAt;
    }
  };
}

// State Selectors //
function getLiveBlogByEventId(state: State, eventId: string): LiveEvent | void {
  const liveEvent = (state?.features?.liveBlog?.liveEvents || []).find(
    (event: LiveEvent) => event.id === eventId
  );

  if (!liveEvent) {
    return console.error(`Couldn't find Live Event with id: ${eventId}`);
  }

  return liveEvent;
}

export function getLiveBlogEvents(state: State): { liveEvents: LiveEvent[]; eventCount: number } {
  const liveBlogState = state?.features?.liveBlog || {};
  const pageNum = liveBlogState?.pageNum ?? 1;
  const timeFilter = liveBlogState?.timeFilter ?? LiveBlogFilter.Upcoming;
  const sorting = liveBlogState?.sortBy ?? LiveBlogSorting.StartingTime;

  const filteredEvents = (liveBlogState?.liveEvents || []).filter(filterByTimeFilter(timeFilter));
  const eventCount = filteredEvents.length;
  const liveEvents = filteredEvents
    .sort(sortByLiveBlogSorting(sorting))
    .slice((pageNum - 1) * ITEMS_ON_PAGE, pageNum * ITEMS_ON_PAGE);

  return { liveEvents, eventCount };
}

export function getLiveBlogSettings(state: State): Partial<ILiveBlogState> {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { liveEvents, ...settings } = state?.features?.liveBlog || {};
  return settings;
}

export function getLiveBlogReporters(state: State, reporterIds: string[] = []): User[] {
  const globalUsers = state?.moderation?.users?.users;

  return reporterIds
    .map(
      (reporterId: string): User => {
        const reporterUser = globalUsers[reporterId];

        if (!reporterUser) {
          console.error(`Couldn't find Reporter user with id: ${reporterId}`);
        }

        return reporterUser;
      }
    )
    .filter(Boolean);
}

export function getLiveBlogReporterSuggestions(state: State, options): User[] {
  const teamMembers = getUsersOfType(state, TModerationUser.TeamMembers, options);

  return teamMembers.users;
}

export function getLiveBlogTeam(
  state: State,
  reporterIds: string[] = []
): { suggestions: User[]; reporters: User[] } {
  const filterUsersByRoles = (user: User): boolean => {
    const isJournalistOrAdmin = ['journalist', 'admin'].includes(user?.getUserRole());
    const isAlreadyReporter = reporterIds.includes(user?.get('id'));
    return isJournalistOrAdmin && !isAlreadyReporter;
  };

  return {
    reporters: getLiveBlogReporters(state, reporterIds),
    suggestions: getLiveBlogReporterSuggestions(state, { filterBy: filterUsersByRoles })
  };
}

export default liveBlogSlice.reducer;
export const { changePageSettings } = liveBlogSlice.actions;
export * from './actions';
